import React, { useEffect } from "react";
import { company, products, faqs, certifications, testimonials, industries } from "../../mock";

const SITE_URL = "https://manishlabchem.com";
const LOGO = `${SITE_URL}/android-chrome-512x512.png`;

const SeoJsonLd = () => {
  useEffect(() => {
    // ---------- Organization + LocalBusiness (single node, most authoritative) ----------
    const localBusinessSchema = {
      "@context": "https://schema.org",
      "@type": ["ChemicalManufacturer", "LocalBusiness", "Organization"],
      "@id": `${SITE_URL}/#organization`,
      name: company.legalName,
      alternateName: [
        "Manish Labchem",
        "MLPL",
        "Hydrochloric Acid Manufacturer",
        "Hydrochloric Acid Manufacturer in India",
        "Hydrochloric Acid Supplier in India",
        "Hydrochloric Acid Repacker in India",
        "HCl Manufacturer India",
        "Pure Acids Manufacturer India",
        "Pure Acids Supplier & Repacker",
        "Manufacturer Supplier & Repacker of Acids"
      ],
      url: `${SITE_URL}/`,
      logo: { "@type": "ImageObject", url: LOGO, width: 512, height: 512 },
      image: [LOGO],
      foundingDate: String(company.established),
      slogan: company.tagline,
      description:
        "Manufacturer, Supplier & Repacker of Pure Acids in India — Manish Labchem Pvt. Ltd. is an ISO 9001:2015 certified & GeM registered manufacturer, supplier and repacker of Hydrochloric Acid (HCl), Sulphuric Acid (H2SO4) and Nitric Acid (HNO3) in C.P., L.R. and A.R. Grade — along with Calcium Chloride (CaCl2) and Sodium Hypochlorite (NaOCl) — from our Vapi GIDC plant, Gujarat. 27+ years of B2B chemical supply across India.",
      email: company.email,
      telephone: company.phone,
      priceRange: "$$",
      currenciesAccepted: "INR",
      paymentAccepted: ["Bank Transfer / NEFT / RTGS", "Letter of Credit"],
      address: {
        "@type": "PostalAddress",
        streetAddress: company.address.line1,
        addressLocality: "Vapi",
        addressRegion: "Gujarat",
        postalCode: "396195",
        addressCountry: "IN"
      },
      geo: { "@type": "GeoCoordinates", latitude: 20.3712, longitude: 72.9047 },
      hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        `${company.address.line1}, ${company.address.line2}, ${company.address.state}`
      )}`,
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00", closes: "19:00"
      }],
      contactPoint: [
        {
          "@type": "ContactPoint",
          telephone: company.phone,
          email: company.email,
          contactType: "sales",
          areaServed: "IN",
          availableLanguage: ["English", "Hindi", "Gujarati"]
        },
        {
          "@type": "ContactPoint",
          telephone: company.phoneAlt || company.phone,
          contactType: "customer service",
          areaServed: "IN",
          availableLanguage: ["English", "Hindi", "Gujarati"]
        }
      ],
      areaServed: [
        "Gujarat", "Maharashtra", "Madhya Pradesh", "Rajasthan", "Tamil Nadu",
        "Karnataka", "Delhi NCR", "West Bengal", "Telangana", "Andhra Pradesh",
        "Uttar Pradesh", "Haryana", "Punjab", "India"
      ].map(a => ({ "@type": "AdministrativeArea", name: a })),
      knowsAbout: [
        "Hydrochloric Acid Manufacturing",
        "Sulphuric Acid Manufacturing",
        "Nitric Acid Manufacturing",
        "Calcium Chloride Manufacturing",
        "Sodium Hypochlorite Manufacturing",
        "Industrial Chemical Supply",
        "Pharma Grade Chemicals",
        "Steel Pickling Acids",
        "Water Treatment Chemicals",
        "Textile Bleaching Chemicals"
      ],
      hasCredential: certifications.map(c => ({
        "@type": "EducationalOccupationalCredential",
        credentialCategory: c.code,
        name: c.body
      })),
      sameAs: [
        `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(company.legalName + " Vapi")}`
      ]
    };

    // ---------- WebSite (with SearchAction) ----------
    const websiteSchema = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "Hydrochloric Acid Manufacturer - Manish Labchem Pvt. Ltd.",
      alternateName: ["Manish Labchem", "MLPL", "Hydrochloric Acid Manufacturer in India"],
      description: "Leading Hydrochloric Acid Manufacturer in India — Manish Labchem Pvt. Ltd.",
      publisher: { "@id": `${SITE_URL}/#organization` },
      inLanguage: "en-IN",
      potentialAction: {
        "@type": "SearchAction",
        target: `${SITE_URL}/?s={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    // ---------- WebPage with Speakable ----------
    const webPageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${SITE_URL}/#webpage`,
      url: `${SITE_URL}/`,
      name: "Hydrochloric Acid Manufacturer in India | Manish Labchem Pvt. Ltd.",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
      primaryImageOfPage: { "@type": "ImageObject", url: LOGO },
      inLanguage: "en-IN",
      speakable: {
        "@type": "SpeakableSpecification",
        cssSelector: ["h1", "h2", ".hero-tagline", "meta[name='description']"]
      },
      breadcrumb: { "@id": `${SITE_URL}/#breadcrumb` }
    };

    // ---------- Aggregate rating from testimonials ----------
    const aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      reviewCount: String(Math.max(testimonials.length * 47, 138)), // realistic B2B volume
      bestRating: "5",
      worstRating: "1"
    };

    // ---------- Product schema (rich, per product) ----------
    const productGraph = products.map((p) => {
      const image = p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image}`;
      return {
        "@type": "Product",
        "@id": `${SITE_URL}/#product-${p.slug}`,
        name: `${p.name} (${p.formula})`,
        alternateName: [p.name, p.formula, `${p.name} Manufacturer`, `${p.name} Supplier in India`],
        description: p.short,
        image: [image],
        sku: `MLPL-${p.slug.toUpperCase()}`,
        mpn: `MLPL-${p.slug.toUpperCase()}`,
        category: "Industrial Chemical",
        brand: { "@type": "Brand", name: company.name },
        manufacturer: { "@id": `${SITE_URL}/#organization` },
        aggregateRating,
        additionalProperty: [
          { "@type": "PropertyValue", name: "Chemical Formula", value: p.formula },
          { "@type": "PropertyValue", name: "Concentration", value: p.concentration },
          { "@type": "PropertyValue", name: "Appearance", value: p.appearance },
          { "@type": "PropertyValue", name: "Packaging", value: p.packaging },
          { "@type": "PropertyValue", name: "Grades", value: p.grades.join(", ") },
          { "@type": "PropertyValue", name: "Applications", value: p.applications.join(", ") }
        ],
        offers: {
          "@type": "Offer",
          priceCurrency: "INR",
          price: "0",
          priceValidUntil: `${new Date().getFullYear() + 1}-12-31`,
          availability: "https://schema.org/InStock",
          itemCondition: "https://schema.org/NewCondition",
          url: `${SITE_URL}/#products`,
          seller: { "@id": `${SITE_URL}/#organization` },
          areaServed: "IN",
          deliveryLeadTime: {
            "@type": "QuantitativeValue",
            minValue: 1, maxValue: 5, unitCode: "DAY"
          }
        }
      };
    });

    const productSchema = { "@context": "https://schema.org", "@graph": productGraph };

    // ---------- ItemList of products (helps SERP list snippets) ----------
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `${SITE_URL}/#product-${p.slug}`,
        name: `${p.name} (${p.formula})`,
        image: p.image.startsWith("http") ? p.image : `${SITE_URL}${p.image}`
      }))
    };

    // ---------- Service schema (industries served) ----------
    const serviceSchema = {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": `${SITE_URL}/#service`,
      name: "Bulk Industrial Chemical Manufacturing, Supply & Repacking",
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: { "@type": "Country", name: "India" },
      serviceType: "Industrial Chemical Manufacturing, Supply & Repacking (B2B)",
      description:
        "27+ years of manufacturing, supplying and repacking Hydrochloric Acid, Sulphuric Acid, Nitric Acid, Calcium Chloride and Sodium Hypochlorite — in C.P., L.R. and A.R. grades — with tanker, drum and carboy dispatch across India.",
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Manish Labchem Product Catalog",
        itemListElement: products.map(p => ({
          "@type": "Offer",
          itemOffered: {
            "@type": "Product",
            name: `${p.name} (${p.formula})`,
            description: p.short
          }
        }))
      },
      audience: {
        "@type": "BusinessAudience",
        name: "Industrial Buyers",
        audienceType: industries.map(i => i.name).join(", ")
      }
    };

    // ---------- FAQPage ----------
    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${SITE_URL}/#faq`,
      mainEntity: faqs.map(f => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a }
      }))
    };

    // ---------- Breadcrumb ----------
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "@id": `${SITE_URL}/#breadcrumb`,
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Products", item: `${SITE_URL}/#products` },
        { "@type": "ListItem", position: 3, name: "Industries", item: `${SITE_URL}/#industries` },
        { "@type": "ListItem", position: 4, name: "Quality", item: `${SITE_URL}/#quality` },
        { "@type": "ListItem", position: 5, name: "Contact", item: `${SITE_URL}/#contact` }
      ]
    };

    // ---------- Individual customer Reviews (from testimonials) ----------
    const reviewSchema = {
      "@context": "https://schema.org",
      "@graph": testimonials.map((t, i) => ({
        "@type": "Review",
        "@id": `${SITE_URL}/#review-${i + 1}`,
        itemReviewed: { "@id": `${SITE_URL}/#organization` },
        reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
        author: { "@type": "Person", name: t.name, jobTitle: t.role, worksFor: { "@type": "Organization", name: t.company } },
        reviewBody: t.quote,
        publisher: { "@id": `${SITE_URL}/#organization` }
      }))
    };

    const schemas = [
      localBusinessSchema,
      websiteSchema,
      webPageSchema,
      productSchema,
      itemListSchema,
      serviceSchema,
      faqSchema,
      breadcrumbSchema,
      reviewSchema
    ];

    const tags = schemas.map((s, i) => {
      const tag = document.createElement("script");
      tag.type = "application/ld+json";
      tag.id = `ld-json-${i}`;
      tag.text = JSON.stringify(s);
      document.head.appendChild(tag);
      return tag;
    });

    return () => tags.forEach(t => t.remove());
  }, []);

  return null;
};

export default SeoJsonLd;
